import { Component, inject, output, computed, signal, effect, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { profileStore, settingStore } from '@core/store';
import { PopoverModule, Popover } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SecurityService, AuthService } from '@core/services';
import { MenuBuilderService, UserRole } from '@shared/services/menu-builder.service';
import { RouterModule } from '@angular/router';
import { ADMIN_ROUTE_CONSTANTS } from '@shared/constants/routes';
import { Tenant, PasswordChange } from '@core/types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { matchValidator } from '@core/utils/validator-extensions.utils';
import { WelcomeTourService } from '@core/services/welcome-tour.service';

interface Language {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  imports: [
    CommonModule,
    PopoverModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    DialogModule,
    PasswordModule,
    ButtonModule,
    InputTextModule,
    FloatLabelModule,
    MessageModule,
    DividerModule
  ],
})
export class HeaderComponent {
  sidebarToggle = output<void>();

  profile = inject(profileStore);
  setting = inject(settingStore);
  private menuBuilderService = inject(MenuBuilderService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  @ViewChild('op') popover!: Popover;

  dropdownOpen = false;
  darkMode = false;

  // Language switcher properties
  languages: Language[] = [
    { code: 'es', name: 'Español', flag: '🇩🇴' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  currentLanguage = signal<Language>(this.languages[0]);

  userInitials = computed(() => {
    const firstName = this.profile.data.firstName();
    const lastName = this.profile.data.lastName();

    if (!firstName && !lastName) return '';

    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';

    return firstInitial + lastInitial;
  });

  tenants = computed(() => this.profile.data.tenants());
  activeTenantSignal = signal<Tenant | null>(null);

  notificationCount = 3;
  securityService = inject(SecurityService);
  private readonly welcomeTour = inject(WelcomeTourService);

  // Change Password State
  changePasswordDialogVisible = signal<boolean>(false);
  passwordForm: FormGroup;
  loading = signal<boolean>(false);

  constructor() {
    // Sincronizar activeTenantSignal con el store
    effect(() => {
      const activeTenant = this.profile.activeTenant();
      this.activeTenantSignal.set(activeTenant);
    });

    // Sincronizar currentLanguage con el settingStore
    effect(() => {
      const lang = this.setting.data.language();
      const language = this.languages.find(l => l.code === lang) || this.languages[0];
      this.currentLanguage.set(language);
    });

    this.passwordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, matchValidator('newPassword')]]
    });

    // Revalidar confirmPassword cuando cambie newPassword
    this.passwordForm.get('newPassword')?.valueChanges.subscribe(() => {
      this.passwordForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.passwordForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  // Password requirement helpers
  hasMinLength(password: string): boolean { return password.length >= 8; }
  hasLowercase(password: string): boolean { return /[a-z]/.test(password); }
  hasUppercase(password: string): boolean { return /[A-Z]/.test(password); }
  hasNumber(password: string): boolean { return /\d/.test(password); }
  hasSpecialChar(password: string): boolean { return /[@$!%*?&]/.test(password); }

  get currentNewPassword(): string {
    return this.passwordForm.get('newPassword')?.value || '';
  }

  onTenantChange(tenant: Tenant | null): void {
    if (!tenant) return;

    // Actualizar el tenant activo en el store
    // El backend validará automáticamente el tenant en cada request mediante el header X-Tenant-Id
    this.securityService.switchTenant(tenant.id);

    // Los condominios se refrescarán automáticamente cuando cambie el tenant
    // El activeTenantSignal se actualizará automáticamente por el effect
  }

  toggleSidebar() {
    // Solo emitir el evento, el layout principal maneja toda la lógica
    this.sidebarToggle.emit();
  }

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    if (this.popover) {
      // Usar el elemento que disparó el evento como target
      const target = event.currentTarget as HTMLElement;
      this.popover.toggle(event, target);
    }
  }

  logout() {
    this.securityService.logout();
  }

  onLanguageChange(language: Language): void {
    if (language && language.code) {
      this.setting.setLanguage(language.code);
    }
  }

  onChangePassword() {
    this.passwordForm.reset();
    this.changePasswordDialogVisible.set(true);
  }

  onSavePassword() {
    if (this.passwordForm.valid) {
      this.loading.set(true);
      const { oldPassword, newPassword } = this.passwordForm.value;
      const model: PasswordChange = { oldPassword, newPassword };

      this.authService.changePassword(model).subscribe({
        next: (response) => {
          if(response.status ==-1) {
            this.loading.set(false);
            this.changePasswordDialogVisible.set(false);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('ALERT.ERROR'),
              detail:   this.translateService.instant('ALERT.CHANGE_PASSWORD_ERROR')
            });
          }else{

            this.loading.set(false);
            this.changePasswordDialogVisible.set(false);
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('ALERT.SAVE_SUCCESS'),
              detail: this.translateService.instant('ALERT.CHANGE_PASSWORD_SUCCESS')
            });
          }
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('ALERT.ERROR'),
            detail: error || this.translateService.instant('ALERT.CHANGE_PASSWORD_ERROR')
          });
        }
      });
    } else {
      Object.keys(this.passwordForm.controls).forEach(key => {
        this.passwordForm.get(key)?.markAsTouched();
      });
    }
  }

  onCancelPassword() {
    this.changePasswordDialogVisible.set(false);
  }

  onStartTour(): void {
    this.welcomeTour.restart();
  }

  protected readonly ADMIN_ROUTE_CONSTANTS = ADMIN_ROUTE_CONSTANTS;
}


